import { Stack } from '@mui/material';
import { ConversationParticipant } from '@provision/entity-types';
import { useForm } from 'react-hook-form';
import { isNullish } from 'remeda';
import { FormAutocomplete } from 'src/components/Forms/FormAutocomplete/form-autocomplete';
import { FormRichTextBubble } from 'src/components/Forms/FormRichTextBubble/form-rich-text-bubble';
import { FormSwitch } from 'src/components/Forms/FormSwitch/form-switch';
import { FormTextField } from 'src/components/Forms/FormTextField/form-text-field';
import { GenericForm } from 'src/components/GenericForm/generic-form';
import { ModalStep, useModalStep } from 'src/components/ModalContainer';
import { Address } from 'src/graphql/entities/address';
import { useSaveClientWithRelatedData } from 'src/graphql/entities/client/hooks/use-save-client-with-related-data';
import { usePhonesAndEmailsLabels } from 'src/graphql/entities/client-label';
import { usePartnersOptions } from 'src/graphql/entities/partner/partner';
import { NullableDeepPartial } from 'src/graphql/types';
import type { ClientWithPartner } from 'src/store/modals';
import { ClientRaw } from 'src/types';
import { useNotifications } from 'src/utils/hooks';
import {
  AddClientFormField,
  type AddClientFormValues,
  getAddClientDefaultValues,
} from './add-client-form';
import { ClientContactRows } from './client-contact-rows';

const NOTES_PLACEHOLDER = `What is the client’s comfort with technology?

Is there anything that should be known about the client when engaging in a support event?`;

export type SavedClient = NullableDeepPartial<ClientRaw>;

interface AddClientStepProps {
  partnerId?: number | null;
  client?: ClientWithPartner | null;
  address?: Partial<Address> | null;
  participant?: Partial<ConversationParticipant> | null;
  onSaved?: (client: SavedClient) => void;
}

/** Create or update a client; pushed as a step, it pops with the saved client. */
export const AddClientStep = ({
  partnerId,
  client,
  address,
  participant,
  onSaved,
}: AddClientStepProps) => {
  const { popStep } = useModalStep<SavedClient>();
  const partnerOptions = usePartnersOptions();
  const { clientEmailLabels, clientPhoneLabels } = usePhonesAndEmailsLabels();
  const { saveClientWithRelatedData, isLoading } = useSaveClientWithRelatedData();
  const { showSuccessAlert } = useNotifications();
  const isUpdate = !!client?.clientId;

  const formMethods = useForm<AddClientFormValues>({
    defaultValues: getAddClientDefaultValues(client),
    mode: 'onTouched',
  });
  const { handleSubmit } = formMethods;

  const onSubmit = handleSubmit(async (values) => {
    const emails = values[AddClientFormField.Emails];
    const phoneNumbers = values[AddClientFormField.PhoneNumbers];
    const primaryEmail = emails.find((row) => row.isPrimary);
    const primaryPhoneNumber = phoneNumbers.find((row) => row.isPrimary);

    const saved = await saveClientWithRelatedData({
      clientId: client?.clientId,
      partnerId: values[AddClientFormField.Partner]?.id ?? partnerId ?? undefined,
      firstName: values[AddClientFormField.FirstName],
      lastName: values[AddClientFormField.LastName],
      notes: values[AddClientFormField.Notes],
      marketingOptOut: values[AddClientFormField.MarketingOptOut],
      primaryEmail: primaryEmail?.value,
      primaryClientEmailId: primaryEmail?.valueId,
      primaryEmailLabelId: primaryEmail?.labelId,
      primaryPhoneNumber: primaryPhoneNumber?.value,
      primaryPhoneNumberLabelId: primaryPhoneNumber?.labelId,
      primaryClientPhoneNumberId: primaryPhoneNumber?.valueId,
      emails: emails.map((row) => ({
        id: row.valueId,
        email: row.value,
        labelId: row.labelId,
      })),
      phoneNumbers: phoneNumbers.map((row) => ({
        id: row.valueId,
        number: parseInt(row.value),
        labelId: row.labelId,
      })),
      address,
      participant,
    });

    if (!saved) {
      return;
    }

    showSuccessAlert(isUpdate ? 'Client was updated' : 'Client was created');
    onSaved?.(saved);
    popStep(saved);
  });

  return (
    <ModalStep
      title={isUpdate ? 'Update client' : 'Create client'}
      testId="add-client-modal"
      actions={[
        { label: 'Cancel', kind: 'secondary', testId: 'add-client-cancel' },
        {
          label: isUpdate ? 'Save' : 'Create client',
          kind: 'primary',
          onClick: onSubmit,
          loading: isLoading,
          testId: 'add-client-submit',
        },
      ]}
    >
      <GenericForm<AddClientFormValues> formMethods={formMethods}>
        {isNullish(partnerId) && (
          <FormAutocomplete
            type="autocomplete"
            name={AddClientFormField.Partner}
            label="Partner"
            required="Partner is required"
            showErrorText
            options={partnerOptions}
            disabled={isLoading}
            data-testid="add-client-partner"
          />
        )}
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
          <FormTextField
            type="textfield"
            name={AddClientFormField.FirstName}
            label="First name"
            required="First name is required"
            showErrorText
            disabled={isLoading}
            fullWidth
            data-testid="add-client-first-name"
          />
          <FormTextField
            type="textfield"
            name={AddClientFormField.LastName}
            label="Last name"
            required="Last name is required"
            showErrorText
            disabled={isLoading}
            fullWidth
            data-testid="add-client-last-name"
          />
        </Stack>
        <ClientContactRows
          name={AddClientFormField.Emails}
          label="Email"
          labelOptions={clientEmailLabels}
          disabled={isLoading}
          testIdPrefix="add-client-email"
        />
        <ClientContactRows
          name={AddClientFormField.PhoneNumbers}
          label="Phone"
          labelOptions={clientPhoneLabels}
          disabled={isLoading}
          testIdPrefix="add-client-phone"
        />
        <FormRichTextBubble
          type="rich-text-bubble"
          name={AddClientFormField.Notes}
          label="Notes"
          placeholder={NOTES_PLACEHOLDER}
          disabled={isLoading}
        />
        <FormSwitch
          type="switch"
          name={AddClientFormField.MarketingOptOut}
          label="Marketing opt-out"
          disabled={isLoading}
          data-testid="add-client-marketing-opt-out"
        />
      </GenericForm>
    </ModalStep>
  );
};
